import { applyLegalMove, legalUciMoves, type AppliedMove, type ChessColor } from "@chessla/shared";
import type { GradeTemporaryMove, TemporaryMoveGrade } from "./temporary-grade";
import { advanceVerifiedLine, createVerifiedLine, type VerifiedLine } from "../review/verified-line";

export interface TemporaryPlayOrigin {
  id: string;
  fen: string;
  format: "rapid";
  playerColor: ChessColor;
  playerRating: number;
  opponentRating: number;
  verifiedLine?: VerifiedLine | null;
  recordedSan?: string | null;
  verifiedAlternativeSan?: string | null;
}

export type TemporaryPlayStatus = "idle" | "evaluating" | "ready" | "unavailable";
export type TemporaryReplySource = "saved_continuation" | "fresh_analysis";

export interface TemporaryPlayState {
  origin: TemporaryPlayOrigin;
  fen: string;
  moves: readonly AppliedMove[];
  status: TemporaryPlayStatus;
  grade: TemporaryMoveGrade | null;
  replySource: TemporaryReplySource | null;
  continuation: readonly string[];
  continuationSan: readonly string[];
  error: string | null;
}

export interface TemporaryPlayDependencies {
  grade: GradeTemporaryMove;
  analyzeReply: (
    fen: string,
    signal: AbortSignal,
  ) => Promise<{ move: string; continuation: readonly string[]; depth: number } | null>;
}

export interface TemporaryPlayController {
  readonly state: TemporaryPlayState;
  move: (from: string, to: string, promotion?: string) => Promise<boolean>;
  reset: () => void;
  dispose: () => void;
}

const sideToMove = (fen: string): ChessColor | null => {
  const side = fen.split(" ")[1];
  return side === "w" || side === "b" ? side : null;
};

export const canPlayerMove = (state: TemporaryPlayState): boolean =>
  (state.status === "idle" || state.status === "ready") &&
  sideToMove(state.fen) === state.origin.playerColor &&
  legalUciMoves(state.fen).length > 0;

const initialState = (origin: TemporaryPlayOrigin): TemporaryPlayState => ({
  origin,
  fen: origin.fen,
  moves: [],
  status: origin.format === "rapid" && sideToMove(origin.fen) === origin.playerColor ? "idle" : "unavailable",
  grade: null,
  replySource: null,
  continuation: [],
  continuationSan: [],
  error: null,
});

const requestedUci = (fen: string, from: string, to: string, promotion?: string): string | null => {
  const base = `${from}${to}`;
  const legal = legalUciMoves(fen).filter((move) => move.startsWith(base));
  if (promotion) return legal.find((move) => move === `${base}${promotion.toLowerCase()}`) ?? null;
  return legal.find((move) => move === base) ?? null;
};

export const createTemporaryPlayController = (
  origin: TemporaryPlayOrigin,
  dependencies: TemporaryPlayDependencies,
  onChange: (state: TemporaryPlayState) => void = () => undefined,
): TemporaryPlayController => {
  let state = initialState(origin);
  let revision = 0;
  let inference: AbortController | null = null;
  const boundOriginLine =
    origin.verifiedLine?.fen === origin.fen
      ? createVerifiedLine(origin.fen, origin.verifiedLine.moves, origin.verifiedLine.depth)
      : null;
  let verifiedLine = boundOriginLine;
  const publish = (update: Partial<TemporaryPlayState>): void => {
    state = { ...state, ...update };
    onChange(state);
  };

  const reset = (): void => {
    revision += 1;
    inference?.abort();
    inference = null;
    verifiedLine = boundOriginLine;
    state = initialState(origin);
    onChange(state);
  };

  const move = async (from: string, to: string, promotion?: string): Promise<boolean> => {
    if (!canPlayerMove(state)) return false;
    const uci = requestedUci(state.fen, from, to, promotion);
    const playerMove = uci ? applyLegalMove(state.fen, uci) : null;
    if (!playerMove) return false;

    const beforeFen = state.fen;
    const branch = ++revision;
    inference?.abort();
    inference = new AbortController();
    const signal = inference.signal;
    publish({
      fen: playerMove.fen,
      moves: [...state.moves, playerMove],
      status: "evaluating",
      grade: {
        status: "pending",
        attemptedSan: playerMove.san,
        evaluation: null,
        deltaCp: null,
        verdict: null,
        consequence: null,
      },
      replySource: null,
      error: null,
    });

    let gradeUnavailable = false;
    const gradePromise = dependencies.grade(beforeFen, playerMove.uci, signal).then((grade) => {
      if (grade.status !== "ready") {
        gradeUnavailable = true;
        if (branch === revision) {
          inference?.abort();
          publish({ grade, status: "unavailable", error: "Objective grading is unavailable." });
        }
        throw new Error("objective-grade-unavailable");
      }
      if (branch === revision) publish({ grade });
      return grade;
    });
    if (legalUciMoves(playerMove.fen).length === 0) {
      try {
        await gradePromise;
      } catch {
        if (branch !== revision) return false;
        publish({
          status: "unavailable",
          error: gradeUnavailable ? "Objective grading is unavailable." : "Objective grading was interrupted.",
        });
        return true;
      }
      if (branch === revision) publish({ status: "ready" });
      return true;
    }

    const advanced = advanceVerifiedLine(verifiedLine, playerMove.uci);
    const savedReply = advanced?.moves[0] ? applyLegalMove(playerMove.fen, advanced.moves[0]) : null;
    let selectedReply: {
      move: string;
      continuation: readonly string[];
      depth: number | null;
      source: TemporaryReplySource;
    } | null = savedReply
      ? { move: savedReply.uci, continuation: advanced!.moves, depth: advanced!.depth, source: "saved_continuation" }
      : null;
    try {
      const [, analyzed] = await Promise.all([
        gradePromise,
        selectedReply ? Promise.resolve(null) : dependencies.analyzeReply(playerMove.fen, signal),
      ]);
      if (!selectedReply && analyzed) {
        const line = createVerifiedLine(playerMove.fen, analyzed.continuation, analyzed.depth);
        if (line?.moves[0] === analyzed.move)
          selectedReply = {
            move: analyzed.move,
            continuation: line.moves,
            depth: analyzed.depth,
            source: "fresh_analysis",
          };
      }
    } catch {
      if (branch !== revision) return false;
      inference?.abort();
      publish({
        status: "unavailable",
        error: gradeUnavailable ? "Objective grading is unavailable." : "Objective grading was interrupted.",
      });
      return true;
    }
    if (branch !== revision) return false;

    if (!selectedReply) {
      publish({ status: "unavailable", error: "Nikola could not verify a legal reply." });
      return true;
    }
    const reply = applyLegalMove(playerMove.fen, selectedReply.move);
    if (!reply) {
      publish({ status: "unavailable", error: "Nikola could not verify a legal reply." });
      return true;
    }
    verifiedLine =
      selectedReply.source === "saved_continuation"
        ? advanceVerifiedLine(advanced, reply.uci)
        : { fen: playerMove.fen, moves: [...selectedReply.continuation], depth: selectedReply.depth };
    if (verifiedLine && selectedReply.source === "fresh_analysis")
      verifiedLine = advanceVerifiedLine(verifiedLine, reply.uci);
    let continuationFen = playerMove.fen;
    const continuationSan = selectedReply.continuation.map((uci) => {
      const move = applyLegalMove(continuationFen, uci)!;
      continuationFen = move.fen;
      return move.san;
    });
    publish({
      fen: reply.fen,
      moves: [...state.moves, reply],
      replySource: selectedReply?.source ?? null,
      continuation: selectedReply.continuation,
      continuationSan,
      status: "ready",
      error: null,
    });
    return true;
  };

  return {
    get state() {
      return state;
    },
    move,
    reset,
    dispose: () => {
      revision += 1;
      inference?.abort();
      inference = null;
    },
  };
};
